import axios from 'axios';
import { Notice, CreateNoticeRequest, UpdateNoticeRequest, NoticePaginationResponse, NoticePriority, NoticeCategory } from '../types/notice';
import { handleUnauthorized } from '../store/authStore';

// Node.js API 기본 URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api2';

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 (인증 토큰 추가)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nas_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 (에러 처리)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

// PHP API 응답을 우리 타입으로 변환하는 함수
const mapPhpResponseToNotice = (phpData: any): Notice => {
  return {
    id: parseInt(phpData.id),
    target_user: phpData.target_user || phpData.user_name, // 새로운 필드명 우선, 기존 필드명 호환성 유지
    note_datetime: phpData.note_datetime,
    note: phpData.note,
    remark: phpData.remark || '',
    priority: phpData.priority || '보통',
    category: phpData.category || '일상지원',
    created_at: phpData.created_at,
  };
};

// 우리 타입을 PHP API 형식으로 변환하는 함수
const mapNoticeToPhpRequest = (data: CreateNoticeRequest | UpdateNoticeRequest) => {
  return {
    target_user: data.target_user, // 새로운 필드명 사용
    note_datetime: data.note_datetime,
    note: data.note,
    remark: data.remark || '',
    priority: data.priority || '보통',
    category: data.category || '일상지원',
    ...(('id' in data) && { id: data.id }), // 수정시에만 id 포함
  };
};

export const noticesApiService = {
  // 오늘 등록된 특이사항 개수 조회
  async getTodayNoticesCount(): Promise<number> {
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const response = await fetch(`${API_BASE_URL}/notes.php?date=${todayStr}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const allNotices = await response.json();
      return allNotices.filter((notice: any) =>
        notice.note_datetime?.startsWith(todayStr)
      ).length;
    } catch (error) {
      console.error('오늘 특이사항 개수 조회 실패:', error);
      return 0;
    }
  },

  // 특이사항 목록 조회
  async getNotices(): Promise<Notice[]> {
    try {
      const response = await apiClient.get('/notes.php');

      // PHP API는 배열을 직접 반환하므로 매핑
      if (Array.isArray(response.data)) {
        return response.data.map(mapPhpResponseToNotice);
      }

      // 응답 데이터가 배열이 아닌 경우
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data.data.map(mapPhpResponseToNotice);
      }

      console.warn('⚠️ 예상하지 못한 응답 형식:', response.data);

      if (import.meta.env.MODE === 'development') {
        return this.getDummyNotices();
      }

      return [];
    } catch (error: any) {
      console.error('❌ 특이사항 목록 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });

      // 네트워크 오류나 서버 오류에 따라 다른 메시지 제공
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        throw new Error('네트워크 연결을 확인해주세요.');
      } else if (error.response?.status === 404) {
        throw new Error('특이사항 API를 찾을 수 없습니다.');
      } else if (error.response?.status >= 500) {
        throw new Error('서버 오류가 발생했습니다.');
      }

      // 개발 환경에서는 더미 데이터로 fallback
      if (import.meta.env.MODE === 'development') {
        return this.getDummyNotices();
      }

      throw new Error('특이사항 목록을 불러오는데 실패했습니다.');
    }
  },

  // 특이사항 생성
  async createNotice(data: CreateNoticeRequest): Promise<Notice> {
    try {
      const phpData = mapNoticeToPhpRequest(data);

      // PHP API는 FormData 방식을 사용하므로 변환
      const formData = new FormData();
      Object.entries(phpData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      const response = await apiClient.post('/notes.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // PHP API 응답이 성공적이면 (상태코드 200-299)
      if (response.status >= 200 && response.status < 300) {
        // 캐시 무효화
        this.invalidateCache();

        // 전체 목록을 다시 조회해서 가장 최근 항목 반환
        const notices = await this.getNotices();
        if (notices.length > 0) {
          // ID 기준으로 가장 큰 값 (최신 항목) 반환
          const latestNotice = notices.reduce((latest, current) =>
            current.id > latest.id ? current : latest
          );
          return latestNotice;
        }
      }

      throw new Error('특이사항 생성에 실패했습니다.');
    } catch (error) {
      console.error('❌ 특이사항 생성 실패:', error);
      throw new Error('특이사항을 저장하는데 실패했습니다.');
    }
  },

  // 특이사항 수정
  async updateNotice(data: UpdateNoticeRequest): Promise<Notice> {
    try {
      const phpData = mapNoticeToPhpRequest(data);

      const response = await apiClient.put('/notes.php',
        new URLSearchParams(phpData as unknown as Record<string, string>).toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      // PHP API 응답이 성공적이면 (상태코드 200-299)
      if (response.status >= 200 && response.status < 300) {
        // 캐시 무효화
        this.invalidateCache();

        // 수정된 데이터를 반환하기 위해 다시 조회
        const notices = await this.getNotices();
        const updatedNotice = notices.find(notice => notice.id === data.id);

        if (updatedNotice) {
          return updatedNotice;
        }
      }

      throw new Error('특이사항 수정에 실패했습니다.');
    } catch (error) {
      console.error('❌ 특이사항 수정 실패:', error);
      throw new Error('특이사항을 수정하는데 실패했습니다.');
    }
  },

  // 특이사항 삭제
  async deleteNotice(id: number): Promise<void> {
    try {
      const response = await apiClient.delete('/notes.php', {
        data: new URLSearchParams({ id: String(id) }).toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // PHP API 응답이 성공적이면 (상태코드 200-299)
      if (response.status >= 200 && response.status < 300) {
        // 캐시 무효화
        this.invalidateCache();
        return;
      }

      throw new Error('특이사항 삭제에 실패했습니다.');
    } catch (error) {
      console.error('❌ 특이사항 삭제 실패:', error);
      throw new Error('특이사항을 삭제하는데 실패했습니다.');
    }
  },

  // 페이지네이션을 위한 래퍼 함수 (PHP API는 페이지네이션 미지원이므로 클라이언트에서 처리)
  async getNoticesWithPagination(
    page: number = 1,
    limit: number = 20,
    search?: string,
    sortField?: keyof Notice,
    sortOrder?: 'asc' | 'desc'
  ): Promise<NoticePaginationResponse> {
    try {
      // 캐시된 데이터 사용으로 성능 향상
      const allNotices = await this.getCachedNotices();

      // 검색 필터링 (다중 검색어 지원: + 구분자)
      let filteredNotices = allNotices;
      if (search && search.trim()) {
        const searchTerms = search.split('+').map(term => term.trim().toLowerCase()).filter(term => term !== '');

        filteredNotices = allNotices.filter(notice => {
          // 모든 검색어가 포함되어야 함 (AND 조건)
          return searchTerms.every(term =>
            notice.note.toLowerCase().includes(term) ||
            notice.target_user.toLowerCase().includes(term) ||
            (notice.remark && notice.remark.toLowerCase().includes(term))
          );
        });
      }

      // 정렬 적용
      if (sortField && sortOrder) {
        filteredNotices.sort((a, b) => {
          const aValue = String(a[sortField] || '');
          const bValue = String(b[sortField] || '');

          if (sortOrder === 'desc') {
            return bValue.localeCompare(aValue);
          } else {
            return aValue.localeCompare(bValue);
          }
        });
      } else {
        // 기본 정렬: 등록일시 내림차순
        filteredNotices.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }

      // 페이지네이션
      const total = filteredNotices.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const data = filteredNotices.slice(startIndex, endIndex);

      return {
        data,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrevious: page > 1,
      };
    } catch (error) {
      console.error('❌ 페이지네이션 조회 실패:', error);
      throw error;
    }
  },

  // 전체 데이터 캐싱을 위한 메서드
  _cachedNotices: null as Notice[] | null,
  _cacheTimestamp: 0,
  _cacheExpiry: 2 * 60 * 1000, // 2분으로 단축 (더 빈번한 업데이트 반영)

  async getCachedNotices(): Promise<Notice[]> {
    const now = Date.now();

    // 캐시가 유효한지 확인
    if (this._cachedNotices && (now - this._cacheTimestamp) < this._cacheExpiry) {
      return this._cachedNotices;
    }

    // 캐시가 없거나 만료된 경우 새로 가져오기
    const notices = await this.getNotices();
    this._cachedNotices = notices;
    this._cacheTimestamp = now;

    return notices;
  },

  // 캐시 무효화
  invalidateCache(): void {
    this._cachedNotices = null;
    this._cacheTimestamp = 0;
  },

  // 전체 특이사항 가져오기 (엑셀 내보내기용)
  async getAllNotices(): Promise<Notice[]> {
    try {
      return await this.getCachedNotices();
    } catch (error) {
      console.error('❌ 전체 특이사항 조회 실패:', error);
      throw new Error('전체 특이사항 데이터를 불러오는데 실패했습니다.');
    }
  },

  // 특정 이용자의 특이사항 조회
  async getNoticesByUserId(userName: string): Promise<Notice[]> {
    try {
      const allNotices = await this.getCachedNotices();

      // 이용자 이름으로 필터링 (target_user 필드 사용)
      return allNotices.filter(notice => notice.target_user === userName);
    } catch (error) {
      console.error('❌ 이용자별 특이사항 조회 실패:', error);
      throw new Error('이용자별 특이사항 데이터를 불러오는데 실패했습니다.');
    }
  },

  // 개발용 더미 데이터
  getDummyNotices(): Notice[] {
    const currentDate = new Date().toISOString();
    const yesterday = new Date(Date.now() - 86400000).toISOString();
    const twoDaysAgo = new Date(Date.now() - 172800000).toISOString();

    return [
      {
        id: 1,
        target_user: '김철수',
        note_datetime: currentDate,
        note: '오후에 다른 이용자와 갈등이 발생하여 소리지름',
        remark: '스포츠 활동 중 발생',
        priority: NoticePriority.HIGH,
        category: NoticeCategory.BEHAVIOR,
        created_at: currentDate
      },
      {
        id: 2,
        target_user: '김철수',
        note_datetime: yesterday,
        note: '아침 식사 거부, 간식은 정상적으로 섭취',
        remark: '스트레스로 인한 식욕부진 추정',
        priority: NoticePriority.MEDIUM,
        category: NoticeCategory.DAILY_CARE,
        created_at: yesterday
      },
      {
        id: 3,
        target_user: '김철수',
        note_datetime: twoDaysAgo,
        note: '병원 진료 후 감기약 처방받음',
        remark: '기침과 콧물 증상',
        priority: NoticePriority.MEDIUM,
        category: NoticeCategory.MEDICAL,
        created_at: twoDaysAgo
      },
      {
        id: 4,
        target_user: '이영희',
        note_datetime: new Date(Date.now() - 259200000).toISOString(),
        note: '가족 면회 시 기분이 좋아짐',
        remark: '어머니와 1시간 대화',
        priority: NoticePriority.LOW,
        category: NoticeCategory.EXTERNAL,
        created_at: new Date(Date.now() - 259200000).toISOString()
      },
      {
        id: 5,
        target_user: '박민수',
        note_datetime: new Date(Date.now() - 345600000).toISOString(),
        note: '투약 시간에 약 복용 거부',
        remark: '약의 쓴맛을 싫어함',
        priority: NoticePriority.HIGH,
        category: NoticeCategory.MEDICAL,
        created_at: new Date(Date.now() - 345600000).toISOString()
      },
      {
        id: 6,
        target_user: '박민수',
        note_datetime: new Date(Date.now() - 432000000).toISOString(),
        note: '저녁에 갑자기 울음이 시작됨',
        remark: '원인 불명, 30분간 지속',
        priority: NoticePriority.MEDIUM,
        category: NoticeCategory.BEHAVIOR,
        created_at: new Date(Date.now() - 432000000).toISOString()
      }
    ];
  }
};

export default noticesApiService;